import { Button, dialog } from "nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import { GetCurrentManilaTimeAPICall } from "../../../components/common/Converter";
import InputFactory from "../../../components/InputFactory";

class BiometricsPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      objects.sort((a, b) => {
        const nameA = a?.name || "";
        const nameB = b?.name || "";

        const startsWithNumberA = /^\d/.test(nameA);
        const startsWithNumberB = /^\d/.test(nameB);

        if (startsWithNumberA && !startsWithNumberB) return -1; // numbers last
        if (!startsWithNumberA && startsWithNumberB) return 1; // letters first

        return nameA.localeCompare(nameB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
      // const keys = ["name"];
      // const [regions, offices, divisions, sections, units, clusters] =
      //   await Promise.all([
      //     findObjectUseCase().execute("Region", {
      //       where: { status: "active" },
      //       keys: keys,
      //     }),
      //     findObjectUseCase().execute("offices", {
      //       where: { status: "active" },
      //       keys: keys,
      //     }),
      //     findObjectUseCase().execute("Division", {
      //       where: { status: "active" },
      //       keys: keys,
      //     }),
      //     findObjectUseCase().execute("Sections", {
      //       where: { status: "active" },
      //       keys: keys,
      //     }),
      //     findObjectUseCase().execute("Units", {
      //       where: { status: "active" },
      //       keys: keys,
      //     }),
      //     findObjectUseCase().execute("Clusters", {
      //       where: { status: "active" },
      //       keys: keys,
      //     }),
      //   ]);

      const dateNow = await GetCurrentManilaTimeAPICall();
      objects.forEach((object) => {
        // Use UTC timestamps
        const update = new Date(object.updated).getTime(); // Convert to UTC timestamp
        // console.log("update: ", update);
        // Calculate difference in seconds
        const difference = (dateNow - update) / 1000;
        const stats = difference > 70 ? "offline" : "online";
        // console.log(`${object?.deviceName}`, update);

        // Update the object's status field
        object.stats = stats;
      });

      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  actionConfirmation(action, message, object) {
    dialog.fire({
      html: (
        <div className="pt-4 pe-4 ps-4">
          <div className="row">
            <div className="col-lg-11 d-flex gap-2">
              <i
                className="bi bi-question-circle"
                style={{
                  zoom: 2,
                  color: "#faad14",
                }}
              />
              <p className="fw-bold">{message}</p>
            </div>
          </div>
          <p style={{ color: "rgba(0, 0, 0, .65)" }}>
            Please click on OK to confirm changes.
          </p>

          <div className="w-100 d-flex justify-content-end gap-4 m-4 pe-3">
            <Button
              className="btn btn-light fs-sm"
              onClick={() => dialog.close()}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={() => {
                if (action === "clearLogs") {
                  this.handleBioCommand(object, "CLEAR LOG");
                } else if (action === "clearData") {
                  this.handleBioCommand(object, "CLEAR DATA");
                } else {
                  this.handleBioCommand(object, "REBOOT");
                }
              }}
            >
              OK
            </Button>
          </div>
        </div>
      ),
      footer: false,
    });
  }

  async handleBioCommand(object, type) {
    try {
      await this.upsertUseCase.execute("biometric_commands", {
        type,
        device: { id: object.id },
      });
      dialog.close();
      this.view.refetchData();
    } catch (e) {
      this.view.showError(e.message || e);
    }
  }

  async submitBiometricLogSync(value, object) {
    const [startDate, endDate] = value;
    const formattedStartDate = `${startDate} 00:00:00`;
    const formattedEndDate = `${endDate} 23:59:59`;

    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const dayMs = 1000 * 60 * 60 * 24;
    const days = Math.max(1, Math.floor((end - start) / dayMs) + 1);
    const loadingSeconds = days * 3;

    const command = [
      {
        type: "DATA QUERY ATTLOG",
        device: { id: object.id },
        parameters: {
          StartTime: formattedStartDate,
          EndTime: formattedEndDate,
        },
      },
      {
        type: "DATA QUERY ATTPHOTO",
        device: { id: object.id },
        parameters: {
          StartTime: formattedStartDate,
          EndTime: formattedEndDate,
        },
      },
    ];

    this.view.setState({ syncLoading: true });
    const startedAt = Date.now();

    try {
      for (const cmd of command) {
        await this.upsertUseCase.execute("biometric_commands", cmd);
      }
      const elapsedMs = Date.now() - startedAt;
      const minLoadingMs = loadingSeconds * 1000;
      if (elapsedMs < minLoadingMs) {
        await new Promise((r) => setTimeout(r, minLoadingMs - elapsedMs));
      }
      this.view.setState({
        syncLogs: false,
        dateRange: [],
        syncLoading: false,
      });
      this.view.showSuccess(
        "Device sync done. Logs are now being synchronized.",
      );
    } catch (e) {
      this.view.setState({ syncLoading: false });
      this.view.showError(e.message || e);
    }
  }
}
export default BiometricsPresenter;
